import React, { useContext, useEffect, useRef } from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { FlatList } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import moment from 'moment'
import { appointmentKindsSelector } from '@src/selectors/app'
import { Button, Text } from '@components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import { AppStackParamList, RootStackParamList } from '@src/navigation/types'
import { NavigatorContext, useSnack } from '@src/utils'
import { ErrorMessages } from '@src/config'
import { NavigationContainer } from '@src/screens/Common/containers'
import { LoadingIndicator } from '@src/components/LoadingIndicator'
import { EhrAppointmentStateKind } from '@src/types'
import { ehrEarlierAvailableSlotsByAppointmentIdSelector } from '../models/nutritionistHub.selectors'
import { SuggestedCallCard } from './UpcomingCalls/SuggestedCallCard'
import { CallCard } from './UpcomingCalls/CallCard'
import { useAppointment } from './VideoCallModal/useAppointment'

const DEFAULT_TITLE = 'Video Call'

const SLOT_HEIGHT = 128

export const RescheduleVideoCallToEarlierSlot = () => {
  const styles = useStyleSheet(themedStyles)
  const dispatch = useDispatch()
  const insets = useSafeAreaInsets()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const showSnack = useSnack()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const reschedulingRef = useRef(false)

  const route = useRoute<RouteProp<RootStackParamList, 'RescheduleVideoCallToEarlierSlot'>>()
  const { appointmentId, appointmentKind, nutritionistName, title, date } = route.params

  const earlierAvailableSlotsByAppointmentId = useSelector(
    ehrEarlierAvailableSlotsByAppointmentIdSelector,
  )
  const appointmentKinds = useSelector(appointmentKindsSelector)

  const { appointment, loading } = useAppointment({
    id: appointmentId,
    skip: !appointmentId,
  })

  useEffect(() => {
    if (!appointment) {
      return
    }

    const canModifyAppointment = [
      EhrAppointmentStateKind.Scheduled,
      EhrAppointmentStateKind.InReview,
    ].includes(appointment.lastState.kind)

    if (moment(appointment.meetingAt).isBefore(moment())) {
      showSnack('This appointment has already passed.', null, 'error')
      navigation.goBack()
      return
    }

    if (!canModifyAppointment) {
      showSnack('This appointment can not be rescheduled.', null, 'error')
      navigation.goBack()
      return
    }
  }, [appointment, navigation, showSnack])

  useEffect(() => {
    dispatch({
      type: 'nutritionistHub/fetchEhrEarlierAvailableSlots',
      payload: { appointmentId },
    })
  }, [dispatch, appointmentId])

  useEffect(
    () => () => {
      if (reschedulingRef.current) {
        disableLoader()
        reschedulingRef.current = false
      }
    },
    [disableLoader],
  )

  const earlierAvailableSlotsForAppointment =
    earlierAvailableSlotsByAppointmentId[appointmentId] || []
  const appointmentKindConfig = appointmentKinds.find((config) => config.key === appointmentKind)
  const navigationBarTitle = `Reschedule ${title || appointmentKindConfig?.title || DEFAULT_TITLE}`

  const openCalendar = () => {
    navigation.replace('CallScheduling', {
      appointmentKind,
      appointmentId,
      allowBackNavigation: true,
    })
  }

  const rescheduleCall = (date: string) => {
    if (reschedulingRef.current) {
      return
    }

    reschedulingRef.current = true
    enableLoader()

    dispatch({
      type: 'nutritionistHub/rescheduleAppointment',
      payload: {
        date,
        id: appointmentId,
      },
      success: () => {
        navigation.replace('Drawer', {
          screen: 'Dashboard',
          params: { screen: 'NutritionistHub' },
        })
      },
      failure: (error: any) => {
        dispatch({
          type: 'nutritionistHub/fetchEhrEarlierAvailableSlots',
          payload: { appointmentId },
        })
        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error', 'x', undefined, 'redesigned')
      },
      complete: () => {
        disableLoader()
        reschedulingRef.current = false
      },
    })
  }

  return (
    <NavigationContainer
      title={navigationBarTitle}
      navigationBarProps={{ titleStyle: styles.navigationTitle }}
    >
      {loading ? (
        <LoadingIndicator />
      ) : (
        <>
          <Text type="regular" style={styles.title}>
            Select and reschedule your initial call to a sooner available slot that works for you
          </Text>
          <FlatList
            ListHeaderComponent={() => (
              <CallCard
                label="Current slot"
                meetingAt={date}
                coachName={nutritionistName}
                appointmentKind={appointmentKind}
                recurring={false}
                style={styles.slotCard}
              />
            )}
            data={earlierAvailableSlotsForAppointment}
            renderItem={({ item: slot }) => (
              <SuggestedCallCard
                key={slot.time}
                style={styles.slotCard}
                label="Suggested"
                meetingAt={slot.time}
                appointmentKind={appointmentKind}
                onSchedulePress={() => {
                  rescheduleCall(slot.time)
                }}
              />
            )}
            keyExtractor={(item) => item.time}
            getItemLayout={(_, index) => ({
              length: SLOT_HEIGHT,
              offset: SLOT_HEIGHT * index,
              index,
            })}
          />
          <Button
            type="outline"
            size="block"
            style={[styles.calendarButton, { marginBottom: Math.max(insets.bottom, 16) }]}
            onPress={openCalendar}
            accessibilityLabel="See Calendar"
          >
            See Calendar
          </Button>
        </>
      )}
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  navigationTitle: {
    maxWidth: '90%',
  },
  title: {
    maxWidth: 280,
    marginTop: 8,
    marginBottom: 24,
    textAlign: 'center',
    alignSelf: 'center',
  },
  alert: {
    marginVertical: 16,
    marginHorizontal: 16,
  },
  calendarButton: {
    marginVertical: 16,
    marginHorizontal: 16,
  },
  slotCard: {
    marginHorizontal: 16,
    marginBottom: 32,
  },
  sectionText: {
    color: 'theme.text.secondary',
    marginLeft: 32,
    marginBottom: 16,
  },
})
